<script setup lang="ts">
import UpdatePrompt from '~/components/UpdatePrompt.vue'
import Dialog from '~/components/common/Dialog.vue'
import Alert from '~/components/common/Alert.vue'
import ToastAlert from '~/components/common/ToastAlert.vue'
import { cache, sessionCache } from '~/common/useCache'
import { jwtDecode } from 'jwt-decode'

useHead({
  meta: [
    {
      name: 'apple-mobile-web-app-capable',
      content: 'yes',
    },
    {
      name: 'apple-mobile-web-app-status-bar-style',
      content: 'default',
    },
    {
      name: 'mobile-web-app-capable',
      // content: 'black-translucent'
      content: 'yes',
    },
  ],
  link: [
    // iPhone SE (2nd generation)
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-750-1334.jpg',
      media:
        '(device-width: 375px) and (device-height: 667px) and (-webkit-device-pixel-ratio: 2)',
    },
    // iPhone XR, XS Max, 11, 11 Pro Max
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-828-1792.jpg',
      media:
        '(device-width: 414px) and (device-height: 896px) and (-webkit-device-pixel-ratio: 2)',
    },
    // iPhone X, XS, 11 Pro, 12 mini, 13 mini
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-1125-2436.jpg',
      media:
        '(device-width: 375px) and (device-height: 812px) and (-webkit-device-pixel-ratio: 3)',
    },
    // iPhone 12, 12 Pro, 13, 13 Pro
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-1170-2532.jpg',
      media:
        '(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3)',
    },
    // iPhone 12 Pro Max, 13 Pro Max
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-1284-2778.jpg',
      media:
        '(device-width: 428px) and (device-height: 926px) and (-webkit-device-pixel-ratio: 3)',
    },
    // iPad
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-1620-2160.jpg',
      media:
        '(device-width: 810px) and (device-height: 1080px) and (-webkit-device-pixel-ratio: 2)',
    },
    // iPad Pro 10.5"
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-1668-2224.jpg',
      media:
        '(device-width: 834px) and (device-height: 1112px) and (-webkit-device-pixel-ratio: 2)',
    },
    // iPad Pro 12.9"
    {
      rel: 'apple-touch-startup-image',
      href: '/splash/apple-splash-2048-2732.jpg',
      media:
        '(device-width: 1024px) and (device-height: 1366px) and (-webkit-device-pixel-ratio: 2)',
    },
  ],
})

const authStore = useAuthStore()

const tokenRefresh = (token: string) => {
  let refresh = false
  //jwt 토큰 만료시간이 1시간 이내라면 갱신
  try {
    let decoded = jwtDecode(token)
    const now = new Date()
    // @ts-ignore
    const exp = new Date(decoded.exp * 1000)
    const diff = exp.getTime() - now.getTime()
    const diffHours = diff / (60 * 60 * 1000)

    if (diffHours <= 0) {
      console.log('✔️토큰이 만료되었습니다.')
      refresh = true
    } else if (diffHours <= 24) {
      console.log('️✔️토큰이 24시간 이내에 만료됩니다.')
      refresh = true
    } else {
      console.log('✔️토큰이 유효합니다.')
    }
  } catch (e) {
    console.error(e)
  }

  if (!refresh) {
    // 토큰이 만료되었을 경우
    return
  }

  const isRefresh = authStore.refreshAccessToken()
  if (isRefresh) {
    authStore.tokenLogin()
  }
}

onBeforeMount(async () => {
  const userInfo = useCookie('user')
  const accessToken = sessionCache.get('bus-a-token')
  const refreshToken = cache.get('bus-r-token')

  // 토큰 리다이렉트 페이지는 자동 로그아웃 스킵
  const route = useRoute()
  const router = useRouter()
  if (route.path.startsWith('/redirect/token')) {
    return
  }

  // 1. r.t 가 없으면 로그아웃 처리
  if (!refreshToken) {
    authStore.logout()
    return
  }

  // 2. r.t 있는데 a.t.가 없으면 r.t 확인하여 갱신후 로그인 처리
  // 없거나 만료되었으면 처리
  if (!accessToken || !userInfo.value) {
    const isRefresh = await authStore.refreshAccessToken()
    if (isRefresh) {
      await authStore.tokenLogin()

      // sessionStorage 손실 시 페이지별 처리
      // students 페이지인 경우 selectedIdx 확인
      if (route.path === '/students') {
        const scheduleStore = useScheduleStore()

        // selectedIdx가 없으면 schedule로 리다이렉트
        if (
          scheduleStore.selectedIdx === '' ||
          scheduleStore.selectedIdx === null ||
          scheduleStore.selectedIdx === undefined
        ) {
          await router.push('/schedule')
        }
      }
      // 루트 경로는 schedule로
      else if (route.path === '/' || route.path === '') {
        await router.push('/schedule')
      }
      // 그 외 페이지는 현재 페이지 유지
    } else {
      // 갱신 실패 시 로그인 페이지로
      await router.push('/login')
    }
    return
  }

  // 3. a.t. 있으면 만료 여부 확인 후 갱신
  tokenRefresh(accessToken)
})

/*onMounted(() => {
  console.log('app mounted')
  const authStore = useAuthStore()
})*/
</script>
<template>
  <div>
    <VitePwaManifest />
    <UpdatePrompt />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>

    <Dialog />
    <Alert />
    <ToastAlert />
  </div>
</template>
